import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Truck, Package, FileText, Shield, MapPin, Clock, CheckCircle, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QuoteModal from '../components/modals/QuoteModal';

const LogisticsPage = () => {
  const navigate = useNavigate();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const logisticsServices = [
    {
      icon: FileText,
      title: 'Customs & Documentation',
      description: 'Complete customs clearance, import/export documentation, and regulatory compliance',
      services: ['Customs Declarations', 'Import Licenses', 'Compliance Certificates', 'Tax Documentation']
    },
    {
      icon: Shield,
      title: 'Insurance & Risk Management', 
      description: 'Comprehensive cargo insurance and risk assessment for material transportation',
      services: ['Cargo Insurance', 'Risk Assessment', 'Damage Claims', 'Liability Coverage']
    },
    {
      icon: Truck,
      title: 'Transportation Management',
      description: 'Multi-modal transportation solutions optimized for construction materials',
      services: ['Road Transport', 'Rail Freight', 'Sea Cargo', 'Air Freight']
    },
    {
      icon: Package,
      title: 'Warehousing & Storage',
      description: 'Secure storage facilities with inventory management and just-in-time delivery',
      services: ['Climate-Controlled Storage', 'Inventory Management', 'Cross-Docking', 'JIT Delivery']
    }
  ];

  const trackingFeatures = [
    {
      title: 'Real-time GPS Tracking',
      description: 'Live location updates for all shipments with estimated delivery times',
      icon: MapPin
    },
    {
      title: 'Automated Notifications',
      description: 'SMS and email alerts for shipment milestones and delivery updates',
      icon: Clock
    },
    {
      title: 'Proof of Delivery',
      description: 'Digital signatures, photos, and delivery confirmations for audit trails',
      icon: CheckCircle
    },
    {
      title: 'Multi-Country Coordination',
      description: 'Seamless logistics coordination across all European Union countries',
      icon: Globe
    }
  ];

  const deliveryOptions = [
    {
      type: 'Express Delivery',
      timeline: '24-48 hours',
      description: 'Priority delivery for urgent construction materials',
      coverage: 'Major EU cities'
    },
    {
      type: 'Standard Delivery', 
      timeline: '3-5 business days',
      description: 'Cost-effective delivery for regular material orders',
      coverage: 'All EU countries'
    },
    {
      type: 'Scheduled Delivery',
      timeline: 'Custom schedule',
      description: 'Delivery timed to match your construction project phases',
      coverage: 'Project sites'
    },
    {
      type: 'Bulk Delivery',
      timeline: '5-10 business days',
      description: 'Large volume deliveries with special handling equipment',
      coverage: 'Construction sites'
    }
  ];

  const logisticsStats = [
    { label: 'Delivery Success Rate', value: '99.2%' },
    { label: 'Average Delivery Time', value: '4.2 days' },
    { label: 'Countries Covered', value: '27' },
    { label: 'Monthly Shipments', value: '5,000+' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:bg-gray-800 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-green-100 text-green-800">Logistics Management</Badge>
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                Complete Supply Chain
                <span className="block font-medium">Management</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                End-to-end logistics solutions for construction materials. From customs clearance 
                to final delivery, we manage every aspect of your supply chain.
              </p>
              <div className="flex space-x-4">
                <Button 
                  onClick={() => setIsQuoteModalOpen(true)}
                  size="lg" 
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <Truck size={20} className="mr-2" />
                  Get Logistics Quote
                </Button>
                <Button size="lg" variant="outline">
                  Track Shipment
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?q=80&w=2070" 
                alt="Logistics Management"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Logistics Services */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Logistics Services
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {logisticsServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <IconComponent size={24} className="text-green-600" />
                      </div>
                      <h3 className="text-xl font-medium text-gray-900">{service.title}</h3>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Services Include:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {service.services.map((item, i) => (
                          <div key={i} className="flex items-center text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tracking Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Real-time Tracking & Visibility
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trackingFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Delivery Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Delivery Options
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {deliveryOptions.map((option, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h4 className="font-medium text-gray-900 mb-2">{option.type}</h4>
                  <Badge className="mb-3 bg-green-100 text-green-800">
                    {option.timeline}
                  </Badge>
                  <p className="text-gray-600 text-sm mb-3">{option.description}</p>
                  <p className="text-xs text-gray-500">
                    <strong>Coverage:</strong> {option.coverage}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-green-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-center mb-12">Logistics Performance</h2>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {logisticsStats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-light mb-2">{stat.value}</div>
                <div className="text-green-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Streamline Your Supply Chain
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let our logistics experts handle the complexity while you focus on construction.
          </p>
          <Button 
            onClick={() => setIsQuoteModalOpen(true)}
            size="lg" 
            className="bg-gray-900 hover:bg-gray-800"
          >
            Optimize Your Logistics
          </Button>
        </div>
      </section>

      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </div>
  );
};

export default LogisticsPage;